import { Stack, TextField } from "@mui/material";
import { useState } from "react";
import { DialogContentDivider, FilterDialog, FilterDialogSection } from "../common";
import { handleStateChange } from "../common/filter/helpers";
import { FilterItem } from "../common/filter/types";
import { PlaylistResponse } from "@Shared/schemas/playlists/playlistSchemas";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface FilterCriteria {
  published?: boolean;
  startDateAfter?: Date;
  startDateBefore?: Date;
  endDateAfter?: Date;
  endDateBefore?: Date;
  minScenarios?: number;
}

interface IPlaylistFilterDialog {
  open: boolean;
  onApply: (filter: (playlist: PlaylistResponse) => boolean) => void;
  onClose: () => void;
}

const PlaylistFilterDialog = ({ open, onApply, onClose }: IPlaylistFilterDialog) => {
  const [filters, setFilters] = useState<FilterCriteria>({});

  const [states] = useState<FilterItem[]>([
    { label: "All", value: "all", selected: true },
    { label: "Published", value: true, selected: false },
    { label: "Draft", value: false, selected: false },
  ]);

  const handleStateClick = (index: number) => {
    const newStates = states.map((state, i) => ({
      ...state,
      selected: i === index
    }));
    const selectedState = newStates[index];
    setFilters(prev => ({
      ...prev,
      published: selectedState.value === "all" ? undefined : selectedState.value as boolean
    }));
  };

  const handleApply = (playlist: PlaylistResponse) => {
    // Published status
    if (filters.published !== undefined && playlist.published !== filters.published) {
      return false;
    }

    // Start date range
    if (filters.startDateAfter && (!playlist.start_at || new Date(playlist.start_at) < filters.startDateAfter)) {
      return false;
    }
    if (filters.startDateBefore && (!playlist.start_at || new Date(playlist.start_at) > filters.startDateBefore)) {
      return false;
    }

    // End date range
    if (filters.endDateAfter && (!playlist.end_at || new Date(playlist.end_at) < filters.endDateAfter)) {
      return false;
    }
    if (filters.endDateBefore && (!playlist.end_at || new Date(playlist.end_at) > filters.endDateBefore)) {
      return false;
    }

    // Scenario count
    const scenarioCount = playlist.scenario_count ?? 0;
    if (filters.minScenarios !== undefined && scenarioCount < filters.minScenarios) {
      return false;
    }

    return true;
  };

  const handleClear = () => {
    setFilters({});
    handleStateClick(0); // Reset to "All"
  };

  return (
    <FilterDialog
      open={open}
      onApply={() => {
        onApply(handleApply);
        onClose();
      }}
      onClose={() => {
        handleClear();
        onClose();
      }}
    >
      <Stack spacing={3}>
        <FilterDialogSection 
          title="State" 
          items={states} 
          onClick={handleStateClick} 
        />
        <DialogContentDivider />
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={2}>
            <MobileDateTimePicker
              label="Start Date After"
              value={filters.startDateAfter || null}
              onChange={(date) => setFilters(prev => ({ ...prev, startDateAfter: date as Date }))}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
            <MobileDateTimePicker
              label="Start Date Before"
              value={filters.startDateBefore || null}
              onChange={(date) => setFilters(prev => ({ ...prev, startDateBefore: date as Date }))}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
            <MobileDateTimePicker
              label="End Date After"
              value={filters.endDateAfter || null}
              onChange={(date) => setFilters(prev => ({ ...prev, endDateAfter: date as Date }))}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
            <MobileDateTimePicker
              label="End Date Before"
              value={filters.endDateBefore || null}
              onChange={(date) => setFilters(prev => ({ ...prev, endDateBefore: date as Date }))}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Stack>
        </LocalizationProvider>

        <TextField
          label="Minimum Scenarios"
          type="number"
          value={filters.minScenarios || ''}
          onChange={(e) => setFilters(prev => ({ 
            ...prev, 
            minScenarios: e.target.value ? parseInt(e.target.value) : undefined 
          }))}
          fullWidth
        />
      </Stack>
    </FilterDialog>
  );
};

export default PlaylistFilterDialog;
